import { createContext, useCallback, useContext, useMemo } from 'react'
import { FieldArrayPath, FieldArrayWithId, FieldValues, useFormContext } from 'react-hook-form'
import { useArrayFieldContext } from './ArrayFieldContext'

export type ArrayFieldRowContext<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
> = {
  field: FieldArrayWithId<TFieldValues, TFieldArrayName, TKeyName>
  index: number
  isFirst: boolean
  isLast: boolean
  name: `${TFieldArrayName}.${number}`
  remove: () => void
  id: string
}

const ArrayFieldRowContext = createContext<ArrayFieldRowContext | undefined>(undefined)

export type ArrayFieldRowProviderProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
> = {
  children: React.ReactNode
  field: FieldArrayWithId<TFieldValues, TFieldArrayName, TKeyName>
  index: number
  id?: string
}

export function ArrayFieldRowProvider<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>({
  children,
  index,
  field,
  id: propsId,
  ...value
}: ArrayFieldRowProviderProps<TFieldValues, TFieldArrayName, TKeyName>) {
  const { clearErrors } = useFormContext()
  const {
    fields,
    name,
    remove: baseRemove,
    id: contextId,
  } = useArrayFieldContext<TFieldValues, TFieldArrayName, TKeyName>()

  const remove = useCallback(() => {
    clearErrors(`${name}.${index}`)
    baseRemove(index)
  }, [baseRemove, clearErrors, index, name])

  const context = useMemo(() => {
    const id = propsId ?? `${contextId}-row-${index}`

    const fieldName: `${string}.${number}` = `${name}.${index}`

    return {
      ...value,
      field: field as unknown as FieldArrayWithId,
      index,
      isFirst: index === 0,
      isLast: index === fields.length - 1,
      name: fieldName,
      remove,
      id,
    }
  }, [contextId, field, fields.length, index, name, propsId, remove, value])

  return <ArrayFieldRowContext.Provider value={context}>{children}</ArrayFieldRowContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useArrayFieldRowContext<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>() {
  const context = useContext(ArrayFieldRowContext)

  if (!context) {
    throw new Error('ArrayFieldRowProvider required to use useArrayFieldRowContext')
  }

  return context as unknown as ArrayFieldRowContext<TFieldValues, TFieldArrayName, TKeyName>
}
